import React, { useState, useRef } from 'react';
import { Box, IconButton, Slider, Typography, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import ReactPlayer from 'react-player';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import ReplayIcon from '@mui/icons-material/Replay';
import DownloadIcon from '@mui/icons-material/Download';

const PlayerContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  // Let the player size to its content so elements placed after it appear right below
  height: 'auto',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
}));

const VideoContainer = styled(Box)({
  position: 'relative',
  paddingTop: '56.25%',
  width: '100%',
  backgroundColor: '#000',
  '& > div': { position: 'absolute', top: 0, left: 0 },
});

const ControlsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(1, 2),
  backgroundColor: theme.palette.background.paper,
  borderTop: `1px solid ${theme.palette.divider}`,
}));

const ControlsRow = styled(Box)({ display: 'flex', alignItems: 'center', width: '100%' });

const formatTime = (seconds: number) => {
  if (isNaN(seconds)) return '00:00';
  const date = new Date(seconds * 1000);
  const hh = date.getUTCHours();
  const mm = date.getUTCMinutes();
  const ss = date.getUTCSeconds().toString().padStart(2, '0');
  if (hh) return `${hh}:${mm.toString().padStart(2, '0')}:${ss}`;
  return `${mm}:${ss}`;
};

export interface VideoPlayerProps {
  videoUrl: string;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({ videoUrl }) => {
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const [played, setPlayed] = useState(0);
  const [seeking, setSeeking] = useState(false);
  const [duration, setDuration] = useState(0);
  const playerRef = useRef<ReactPlayer | null>(null);

  const handlePlayPause = () => setPlaying(!playing);
  const handleMute = () => setMuted(!muted);
  const handleVolumeChange = (_: any, newValue: number | number[]) => {
    const v = Array.isArray(newValue) ? newValue[0] : newValue;
    setVolume(v / 100);
    if (v === 0) setMuted(true);
    else if (muted) setMuted(false);
  };

  const handleSeekMouseDown = () => setSeeking(true);
  const handleSeekChange = (_: any, newValue: number | number[]) =>
    setPlayed((Array.isArray(newValue) ? newValue[0] : newValue) / 100);
  const handleSeekMouseUp = (_: any, newValue: number | number[]) => {
    setSeeking(false);
    const v = Array.isArray(newValue) ? newValue[0] : newValue;
    playerRef.current?.seekTo(v / 100);
  };

  const handleProgress = (state: { played: number }) => {
    if (!seeking) setPlayed(state.played);
  };
  const handleDuration = (d: number) => setDuration(d);
  const handleReplay = () => {
    playerRef.current?.seekTo(0);
    setPlaying(true);
  };

  const handleFullscreen = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('Fullscreen button clicked');
    const videoElement = document.querySelector('.react-player video') as any;
    if (videoElement) {
      if (videoElement.requestFullscreen) videoElement.requestFullscreen();
      else if (videoElement.webkitRequestFullscreen) videoElement.webkitRequestFullscreen();
      else if (videoElement.mozRequestFullScreen) videoElement.mozRequestFullScreen();
      else if (videoElement.msRequestFullscreen) videoElement.msRequestFullscreen();
    }
  };

  const handleDownload = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('Download button clicked');
    try {
      let href = videoUrl;
      let filename = 'generated-video.mp4';
      let objectUrl: string | null = null;

      // For external URLs (like S3 presigned URLs), just open in new window
      // The server's Content-Disposition header will trigger the download
      if (href.startsWith('http://') || href.startsWith('https://')) {
        console.log('Opening external URL in new window for download...');
        window.open(href, '_blank');
        return;
      }

      // Convert data URI to Blob for reliable download behavior
      if (href.startsWith('data:')) {
        const match = href.match(/^data:(.*?);base64,(.*)$/);
        const mime = match?.[1] || 'video/mp4';
        const b64 = match?.[2] || '';
        const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
        const blob = new Blob([bytes], { type: mime });
        objectUrl = URL.createObjectURL(blob);
        href = objectUrl;
        // Try to infer extension
        if (mime.includes('webm')) filename = 'generated-video.webm';
        else if (mime.includes('ogg')) filename = 'generated-video.ogv';
      }

      const a = document.createElement('a');
      a.href = href;
      a.download = filename;
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      if (objectUrl) {
        // Clean up after a short delay to ensure download starts
        setTimeout(() => URL.revokeObjectURL(objectUrl), 100);
      }
    } catch (err) {
      console.error('Download failed:', err);
      alert('Failed to download video. Please try again or check console for details.');
    }
  };

  const handleError = (error: any) => console.error('Error in video player:', error);
  const handlePlayError = (error: any) => console.error('Error playing video:', error);

  return (
    <PlayerContainer>
      <VideoContainer>
        <ReactPlayer
          ref={playerRef}
          url={videoUrl}
          width="100%"
          height="100%"
          playing={playing}
          volume={volume}
          muted={muted}
          onProgress={handleProgress}
          onDuration={handleDuration}
          className="react-player"
          controls={false}
          onError={handleError}
          onPlayError={handlePlayError}
        />
      </VideoContainer>

      <ControlsContainer onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
        <ControlsRow>
          <Slider
            min={0}
            max={100}
            value={played * 100}
            onMouseDown={handleSeekMouseDown}
            onChange={handleSeekChange}
            onChangeCommitted={handleSeekMouseUp}
            sx={{ mr: 2 }}
          />
          <Typography variant="body2" sx={{ minWidth: 60 }}>
            {formatTime(played * duration)} / {formatTime(duration)}
          </Typography>
        </ControlsRow>
        <ControlsRow sx={{ mt: 1, justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={handlePlayPause} size="small">
              {playing ? <PauseIcon /> : <PlayArrowIcon />}
            </IconButton>
            <IconButton onClick={handleReplay} size="small">
              <ReplayIcon />
            </IconButton>
            <IconButton onClick={handleMute} size="small">
              {muted ? <VolumeOffIcon /> : <VolumeUpIcon />}
            </IconButton>
            <Slider
              min={0}
              max={100}
              value={muted ? 0 : volume * 100}
              onChange={handleVolumeChange}
              sx={{ width: 100, mx: 1 }}
              size="small"
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title="Download video">
              <IconButton onClick={handleDownload} size="small">
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Fullscreen">
              <IconButton onClick={handleFullscreen} size="small">
                <FullscreenIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </ControlsRow>
      </ControlsContainer>
    </PlayerContainer>
  );
};

export default VideoPlayer;
